import { createElement, type FocusEvent } from "react";
import toast from "react-hot-toast";

type ShareVideoOptions = {
  url?: string | null;
  title?: string | null;
};

function copyTextWithSelection(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.opacity = "0";
  textarea.style.position = "fixed";
  textarea.style.pointerEvents = "none";
  textarea.style.width = "1px";
  textarea.style.height = "1px";
  textarea.style.top = "0";
  textarea.style.left = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  return copied;
}

async function copyTextToClipboard(text: string) {
  if (copyTextWithSelection(text)) return;

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error("Copy command failed");
}

function showManualShareToast(url: string) {
  toast.custom(
    (t) =>
      createElement(
        "div",
        {
          className: `${
            t.visible ? "animate-enter" : "animate-leave"
          } pointer-events-auto w-[360px] max-w-[calc(100vw-32px)] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl`,
        },
        createElement("p", { className: "text-sm font-semibold text-slate-900" }, "Link del video"),
        createElement(
          "p",
          { className: "mt-1 text-xs text-slate-500" },
          "Tu navegador bloqueó la copia automática. Puedes seleccionarlo aquí."
        ),
        createElement("input", {
          readOnly: true,
          value: url,
          onFocus: (event: FocusEvent<HTMLInputElement>) => event.currentTarget.select(),
          className:
            "mt-3 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100",
        }),
        createElement(
          "div",
          { className: "mt-3 flex justify-end gap-2" },
          createElement(
            "button",
            {
              type: "button",
              onClick: () => window.open(url, "_blank", "noopener,noreferrer"),
              className: "rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100",
            },
            "Abrir"
          ),
          createElement(
            "button",
            {
              type: "button",
              onClick: () => toast.dismiss(t.id),
              className: "rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700",
            },
            "Listo"
          )
        )
      ),
    { duration: 12000 }
  );
}

export async function shareVideo({ url, title }: ShareVideoOptions) {
  if (!url) return;

  const shareTitle = title ? `Video de ${title}` : "Video Fluxi";

  try {
    await copyTextToClipboard(url);
    toast.success("Link copiado");
  } catch {
    try {
      if (!("share" in navigator) || typeof navigator.share !== "function") {
        throw new Error("Share API unavailable");
      }
      if (navigator.canShare && !navigator.canShare({ url })) {
        throw new Error("URL cannot be shared");
      }
      await navigator.share({
        title: shareTitle,
        text: "Mira este video creado en Fluxi.",
        url,
      });
    } catch (error) {
      if ((error as Error)?.name === "AbortError") return;
      showManualShareToast(url);
    }
  }
}
